import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  FileBarChart, BarChart2, TrendingUp, DollarSign,
  Download, Printer, Megaphone, Users, MousePointerClick,
  RefreshCw, Check,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Card, PageHeader, Badge } from '../components/Card'
import { analyticsOverview, adsReport, listDrafts } from '../api/client'
import { layout } from '../utils/layout'

// ── Report templates ───────────────────────────────────────────────────────────

type TemplateId = 'performance' | 'content' | 'campaign'

interface Template {
  id: TemplateId
  label: string
  description: string
  icon: typeof BarChart2
  defaultWidgets: WidgetId[]
}

const TEMPLATES: Template[] = [
  {
    id: 'performance',
    label: 'Performance Summary',
    description: 'Reach, impressions, engagement, and growth overview',
    icon: TrendingUp,
    defaultWidgets: ['reach', 'impressions', 'engagement', 'growth', 'reach_chart', 'narrative'],
  },
  {
    id: 'content',
    label: 'Content Report',
    description: 'Draft status, platform breakdown, and publishing velocity',
    icon: FileBarChart,
    defaultWidgets: ['total_drafts', 'approved', 'reach', 'engagement', 'content_chart', 'narrative'],
  },
  {
    id: 'campaign',
    label: 'Campaign Report',
    description: 'Ad spend, ROAS, CTR, CPA, and campaign performance',
    icon: Megaphone,
    defaultWidgets: ['spend', 'roas', 'ctr', 'cpa', 'spend_chart', 'narrative'],
  },
]

// ── Available widgets ──────────────────────────────────────────────────────────

type WidgetId =
  | 'reach' | 'impressions' | 'engagement' | 'growth'
  | 'spend' | 'roas' | 'ctr' | 'cpa'
  | 'total_drafts' | 'approved'
  | 'reach_chart' | 'spend_chart' | 'content_chart'
  | 'narrative'

interface WidgetDef {
  id: WidgetId
  label: string
  category: 'metric' | 'chart' | 'text'
}

const ALL_WIDGETS: WidgetDef[] = [
  { id: 'reach', label: 'Total Reach', category: 'metric' },
  { id: 'impressions', label: 'Impressions', category: 'metric' },
  { id: 'engagement', label: 'Engagement Rate', category: 'metric' },
  { id: 'growth', label: 'Follower Growth', category: 'metric' },
  { id: 'spend', label: 'Ad Spend', category: 'metric' },
  { id: 'roas', label: 'ROAS', category: 'metric' },
  { id: 'ctr', label: 'Click-through Rate', category: 'metric' },
  { id: 'cpa', label: 'Cost per Action', category: 'metric' },
  { id: 'total_drafts', label: 'Total Drafts', category: 'metric' },
  { id: 'approved', label: 'Approved Drafts', category: 'metric' },
  { id: 'reach_chart', label: 'Reach Trend Chart', category: 'chart' },
  { id: 'spend_chart', label: 'Spend Pacing Chart', category: 'chart' },
  { id: 'content_chart', label: 'Content Status Chart', category: 'chart' },
  { id: 'narrative', label: 'AI Narrative Summary', category: 'text' },
]

const RANGES = ['7d', '30d', '90d'] as const
type Range = typeof RANGES[number]

// ── Synthetic chart data helpers ───────────────────────────────────────────────

function reachChartData(reach: number) {
  return Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    reach: Math.round(reach / 7 * (0.7 + Math.sin(i * 0.9) * 0.3 + i * 0.05)),
  }))
}

function spendChartData(spend: number) {
  return Array.from({ length: 7 }, (_, i) => ({
    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    spend: Math.round((spend / 7) * (0.8 + Math.random() * 0.4)),
  }))
}

function contentChartData(total: number, approved: number) {
  const published = Math.round(approved * 0.6)
  const reviewed = Math.round((total - approved) * 0.4)
  const draft = total - approved - reviewed
  return [
    { status: 'Draft', count: Math.max(0, draft) },
    { status: 'Reviewed', count: Math.max(0, reviewed) },
    { status: 'Approved', count: approved },
    { status: 'Published', count: published },
  ]
}

const CHART_COLORS = { primary: '#818cf8', secondary: '#34d399', accent: '#fb923c' }

// ── Widget renderers ───────────────────────────────────────────────────────────

function MetricWidget({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-800/40 rounded-xl p-4 border border-surface-border">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-slate-100">{value}</p>
      {sub && <p className="text-xs text-slate-600 mt-0.5">{sub}</p>}
    </div>
  )
}

// ── Main ReportBuilderView ─────────────────────────────────────────────────────

export default function ReportBuilderView() {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>('performance')
  const [activeWidgets, setActiveWidgets] = useState<Set<WidgetId>>(
    new Set(TEMPLATES[0].defaultWidgets)
  )
  const [range, setRange] = useState<Range>('7d')
  const [printed, setPrinted] = useState(false)

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics-overview', range],
    queryFn: () => analyticsOverview(range),
  })

  const { data: ads, isLoading: adsLoading } = useQuery({
    queryKey: ['ads-report', range],
    queryFn: () => adsReport(range),
  })

  const { data: draftsData } = useQuery({
    queryKey: ['drafts'],
    queryFn: () => listDrafts(undefined, 100),
  })

  const isLoading = analyticsLoading || adsLoading

  function applyTemplate(t: Template) {
    setSelectedTemplate(t.id)
    setActiveWidgets(new Set(t.defaultWidgets))
  }

  function toggleWidget(id: WidgetId) {
    setActiveWidgets(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  function handlePrint() {
    setPrinted(true)
    setTimeout(() => { window.print(); setPrinted(false) }, 100)
  }

  const totalDrafts = draftsData?.total ?? 0
  const approvedDrafts = (draftsData?.drafts ?? []).filter(d => d.status === 'approved' || d.status === 'published').length

  return (
    <div>
      <PageHeader
        title="Report Builder"
        subtitle="Templates · Custom widgets · Analytics export"
        action={
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              {printed ? <Check className="w-4 h-4 text-green-400" /> : <Printer className="w-4 h-4" />}
              Print
            </button>
            <button
              onClick={() => {
                const csv = [
                  ['Metric', 'Value', 'Period'],
                  ['Reach', analytics?.reach ?? 0, range],
                  ['Impressions', analytics?.impressions ?? 0, range],
                  ['Engagement Rate', `${((analytics?.engagement_rate ?? 0) * 100).toFixed(2)}%`, range],
                  ['Follower Growth', analytics?.follower_growth ?? 0, range],
                  ['Ad Spend', `$${ads?.total_spend?.toFixed(2) ?? 0}`, range],
                  ['ROAS', ads?.avg_roas?.toFixed(2) ?? 0, range],
                  ['CTR', `${((ads?.avg_ctr ?? 0) * 100).toFixed(2)}%`, range],
                  ['CPA', `$${ads?.avg_cpa?.toFixed(2) ?? 0}`, range],
                ].map(r => r.join(',')).join('\n')
                const blob = new Blob([csv], { type: 'text/csv' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url; a.download = `socialscale-report-${range}.csv`; a.click()
                URL.revokeObjectURL(url)
              }}
              className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Template + Range selectors */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Templates */}
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Template</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TEMPLATES.map(t => {
                const Icon = t.icon
                return (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${
                      selectedTemplate === t.id
                        ? 'bg-brand-700/20 border-brand-700/50 text-brand-200'
                        : 'bg-slate-800/30 border-surface-border text-slate-400 hover:bg-slate-800/60 hover:text-slate-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{t.label}</div>
                      <div className="text-[11px] mt-0.5 text-slate-600 leading-tight">{t.description}</div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Date range */}
          <div className="shrink-0">
            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Date Range</p>
            <div className="flex gap-1 p-1 bg-slate-800/50 rounded-xl">
              {RANGES.map(r => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${
                    range === r ? 'bg-brand-700/50 text-brand-200 font-medium' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Widget picker */}
        <Card title="Widgets">
          <div className="flex flex-wrap gap-2">
            {ALL_WIDGETS.map(w => (
              <button
                key={w.id}
                onClick={() => toggleWidget(w.id)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  activeWidgets.has(w.id)
                    ? 'bg-brand-700/30 border-brand-700/60 text-brand-200'
                    : 'bg-slate-800/40 border-surface-border text-slate-500 hover:text-slate-300 hover:border-slate-600'
                }`}
              >
                {activeWidgets.has(w.id) && <Check className="w-3 h-3" />}
                {w.label}
                <Badge color={w.category === 'chart' ? 'purple' : w.category === 'text' ? 'blue' : 'slate'}>
                  {w.category}
                </Badge>
              </button>
            ))}
          </div>
        </Card>

        {/* Report preview */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-slate-300">Report Preview</h3>
            {isLoading && <RefreshCw className="w-3.5 h-3.5 text-slate-500 animate-spin" />}
          </div>

          {/* Metric widgets row */}
          {(() => {
            const metricWidgets = [
              { id: 'reach' as WidgetId, label: 'Total Reach', value: analytics?.reach?.toLocaleString() ?? '—', sub: `${range} period`, icon: Users },
              { id: 'impressions' as WidgetId, label: 'Impressions', value: analytics?.impressions?.toLocaleString() ?? '—', sub: `${range} period`, icon: TrendingUp },
              { id: 'engagement' as WidgetId, label: 'Engagement Rate', value: analytics ? `${(analytics.engagement_rate * 100).toFixed(2)}%` : '—', sub: 'vs. prev period', icon: MousePointerClick },
              { id: 'growth' as WidgetId, label: 'Follower Growth', value: analytics?.follower_growth != null ? `+${analytics.follower_growth.toLocaleString()}` : '—', sub: `${range} net new`, icon: TrendingUp },
              { id: 'spend' as WidgetId, label: 'Ad Spend', value: ads ? `$${ads.total_spend.toFixed(2)}` : '—', sub: `${range} total`, icon: DollarSign },
              { id: 'roas' as WidgetId, label: 'ROAS', value: ads ? `${ads.avg_roas.toFixed(2)}x` : '—', sub: 'return on ad spend', icon: TrendingUp },
              { id: 'ctr' as WidgetId, label: 'Click-through Rate', value: ads ? `${(ads.avg_ctr * 100).toFixed(2)}%` : '—', sub: 'avg across campaigns', icon: MousePointerClick },
              { id: 'cpa' as WidgetId, label: 'Cost per Action', value: ads ? `$${ads.avg_cpa.toFixed(2)}` : '—', sub: 'avg CPA', icon: DollarSign },
              { id: 'total_drafts' as WidgetId, label: 'Total Drafts', value: totalDrafts.toString(), sub: 'all statuses', icon: FileBarChart },
              { id: 'approved' as WidgetId, label: 'Approved Drafts', value: approvedDrafts.toString(), sub: 'approved + published', icon: Check },
            ].filter(w => activeWidgets.has(w.id))

            if (!metricWidgets.length) return null
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {metricWidgets.map(w => (
                  <MetricWidget key={w.id} label={w.label} value={w.value} sub={w.sub} />
                ))}
              </div>
            )
          })()}

          {/* Reach trend chart */}
          {activeWidgets.has('reach_chart') && analytics && (
            <Card title={`Reach Trend — ${range}`}>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={reachChartData(analytics.reach)}>
                  <defs>
                    <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={50} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="reach" stroke={CHART_COLORS.primary} strokeWidth={2} fill="url(#reachGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Spend pacing chart */}
          {activeWidgets.has('spend_chart') && ads && (
            <Card title={`Ad Spend Pacing — ${range}`}>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={spendChartData(ads.total_spend)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={50} tickFormatter={v => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [`$${v.toFixed(2)}`, 'Spend']}
                  />
                  <Bar dataKey="spend" fill={CHART_COLORS.accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Content status chart */}
          {activeWidgets.has('content_chart') && (
            <Card title="Content Pipeline Status">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={contentChartData(totalDrafts, approvedDrafts)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="status" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={65} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill={CHART_COLORS.secondary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* AI Narrative */}
          {activeWidgets.has('narrative') && (
            <Card title="AI Narrative Summary">
              {ads?.narrative ? (
                <p className="text-sm text-slate-300 leading-relaxed">{ads.narrative}</p>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  {isLoading
                    ? 'Generating narrative…'
                    : 'Run the ads report to generate an AI-powered narrative summary for this period.'}
                </p>
              )}
            </Card>
          )}

          {/* Empty state */}
          {activeWidgets.size === 0 && (
            <div className="py-12 text-center text-slate-500 text-sm">
              <FileBarChart className="w-12 h-12 mx-auto mb-3 text-slate-700" />
              Select widgets above to build your report.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
